import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Phone, ArrowRight, Truck, MapPin, AlertCircle, Info, X } from 'lucide-react';

export default function RefillPage() {
  const [formData, setFormData] = useState({ name: '', phone: '', rxNumber: '', method: 'pickup', address: '', notes: '' });
  const [showRxInfo, setShowRxInfo] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to top when submission is successful
  useEffect(() => {
    if (submitted) {
      window.scrollTo(0, 0);
    }
  }, [submitted]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?\d{7,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Please enter a valid phone number (only + and digits)';
    }
    if (!formData.rxNumber.trim()) newErrors.rxNumber = 'Rx Number is required';
    if (formData.method === 'delivery' && !formData.address.trim()) {
      newErrors.address = 'Delivery address is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) {
      setSubmitError('Please complete all required fields correctly.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/refill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          rx_number: formData.rxNumber,
          pickup_or_delivery: formData.method
        }),
      });
      if (response.ok) {
        setSubmitted(true);
      } else {
        setSubmitError('Please check the form and try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Please check the form and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center py-20 bg-gradient-to-br from-slate-50 via-cyan-50/30 to-blue-50/50" style={{ minHeight: 'calc(100vh - 5rem)' }}>
        <div className="max-w-md mx-auto text-center px-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Request Sent!</h1>
          <p className="text-slate-600 mb-8">
            Your refill request has been submitted successfully.
            {formData.method === 'delivery'
              ? " We'll contact you before delivery."
              : " We'll notify you when it's ready for pickup."
            }
          </p>
          <Link to="/" className="bg-slate-900 text-white px-6 py-3 rounded-full font-medium hover:bg-slate-800">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 via-cyan-50/30 to-blue-50/50">
      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="container-custom text-center">
          <span className="text-sm font-semibold text-cyan-600 uppercase tracking-wider mb-4 block">Refill Prescription</span>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Quick & Easy <span className="text-slate-500">Refills</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Request a refill in seconds. Choose pickup or free delivery. We'll handle the rest.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-8 pb-16">
        <div className="container-custom">
          <div className="max-w-xl mx-auto bg-white p-8 rounded-3xl shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => { setFormData({ ...formData, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: '' }); }}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.name ? 'border-red-400 bg-red-50/50' : 'border-slate-200'}`}
                  placeholder="Enter your name"
                />
                {errors.name && <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => { const val = e.target.value.replace(/[^+\d]/g, ''); setFormData({ ...formData, phone: val }); if (errors.phone) setErrors({ ...errors, phone: '' }); }}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.phone ? 'border-red-400 bg-red-50/50' : 'border-slate-200'}`}
                  placeholder="(555) 555-5555"
                />
                {errors.phone && <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.phone}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Rx Number *
                  <button
                    type="button"
                    onClick={() => setShowRxInfo(true)}
                    className="inline-flex items-center justify-center w-5 h-5 ml-1.5 rounded-full bg-cyan-100 text-cyan-600 hover:bg-cyan-200 transition-colors align-middle"
                    aria-label="What is Rx Number?"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </label>
                <input
                  type="text"
                  value={formData.rxNumber}
                  onChange={(e) => { setFormData({ ...formData, rxNumber: e.target.value }); if (errors.rxNumber) setErrors({ ...errors, rxNumber: '' }); }}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.rxNumber ? 'border-red-400 bg-red-50/50' : 'border-slate-200'}`}
                  placeholder="Enter your Rx number"
                />
                {errors.rxNumber && <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.rxNumber}</p>}
              </div>

              {/* Pickup or Delivery */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">How would you like to receive it? *</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, method: 'pickup' })}
                    className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${formData.method === 'pickup'
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-slate-200 hover:border-slate-300'
                      }`}
                  >
                    <MapPin className="w-5 h-5 text-slate-600" />
                    <div className="text-left">
                      <p className="font-medium text-slate-900">Pickup</p>
                      <p className="text-xs text-slate-500">At pharmacy</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, method: 'delivery' })}
                    className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${formData.method === 'delivery'
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-slate-200 hover:border-slate-300'
                      }`}
                  >
                    <Truck className="w-5 h-5 text-green-600" />
                    <div className="text-left">
                      <p className="font-medium text-slate-900">Delivery</p>
                      <p className="text-xs text-green-600 font-medium">FREE!</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Address for delivery */}
              {formData.method === 'delivery' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Delivery Address *</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => { setFormData({ ...formData, address: e.target.value }); if (errors.address) setErrors({ ...errors, address: '' }); }}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.address ? 'border-red-400 bg-red-50/50' : 'border-slate-200'}`}
                    placeholder="Enter your full address"
                  />
                  {errors.address && <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.address}</p>}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
                  rows={3}
                  placeholder="Any special instructions?"
                />
              </div>
              {submitError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-center">
                  <p className="text-sm text-red-600 flex items-center justify-center gap-1.5">
                    <AlertCircle className="w-4 h-4" />{submitError}
                  </p>
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 text-white py-4 rounded-full font-medium hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Submit Refill Request'}
                {!loading && <ArrowRight className="w-5 h-5" />}
              </button>
            </form>
          </div>

          {/* Call CTA */}
          <div className="mt-8 text-center">
            <p className="text-slate-600 mb-3">Need it faster? Call us directly.</p>
            <a href="tel:+17187970200" className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-900 px-6 py-3 rounded-full font-medium hover:bg-slate-50">
              <Phone className="w-4 h-4" />
              (718) 797-0200
            </a>
          </div>
        </div>
      </section>
      {/* Rx Info Modal */}
      {showRxInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowRxInfo(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-xs w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-900">Where to find your Rx Number</h3>
              <button
                onClick={() => setShowRxInfo(false)}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-3">
              <img
                src="/rx-reference.png"
                alt="Rx Number location on prescription label"
                className="w-full rounded-lg"
              />
              <p className="mt-2 text-xs text-slate-600 text-center">
                The <strong>RX#</strong> is usually found on the top of your prescription label.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
